<template>
  <tr>
    <td>{{props.index}}</td>
    <td>{{WORK_EVENT_TYPES[item.work]}}{{item.edited_at ? '*' : ''}}</td>
    <td>
      <div class="d-flex justify-content-between">
        <span class="m-0">{{item.spot_name}}</span>
        <SpotCreateModal
          v-if="canCreateSpot"
          :isCreateAuthority="createSpotAuthority"
          :isOperationOffice="true"
          :showFlash="showFlashMessage"
          :item="spotParams"
          @updateItem="callbackCreatedSpot"
          :readOnlyKeys="SPOT_READONLY_KEYS"
          :preMount="assignAddress"
          :draggable="false"
        >
          <template v-slot:button>
            <v-tooltip
              :text="i18n.global.t('pages.dailyReports.updateModal.workList.registSpot')"
            >
              <template v-slot:activator="{ props }">
                <span
                  class="material-symbols-outlined pin-drop"
                  v-bind="props"
                >
                  pin_drop
                </span>
              </template>
            </v-tooltip>
          </template>
        </SpotCreateModal>
      </div>
    </td>
    <td>{{ leavingTime }}</td>
    <td>{{ returningTime }}</td>
    <td>{{ workingTime }}</td>
    <td>{{ drivingTime }}</td>
    <td>{{ mileage }}</td>
    <td>
      <WorkloadUpdateModal
        :isWaitingForSave="props.isWaitingForSave"
        v-if="showWorkloadButton"
        :workItem="item"
        :operationOfficeId="operationOffice.id"
        :updateAuthority="updateAuthority"
        @setWorkId="setWorkId"
        @setRefuelingAmount="setRefuelingAmount"
      />
    </td>
    <td style="width: 70px">
      <button
        class="update-dailyreport-button edit-button"
        v-if="isDisplayEditButton"
        @click="edit"
      >
        <span class="material-symbols-outlined">
          edit
        </span>
      </button>
      <button
        class="update-dailyreport-button delete-button"
        v-if="isDisplayDeleteButton"
        @click="remove"
      >
        <span class="material-symbols-outlined">
          delete
        </span>
      </button>
    </td>
    <td>
      <div
        v-if="item.allocated"
        class="allocated-text"
      >
        {{ i18n.global.t('pages.dailyReports.updateModal.workList.autoAssignment') }}
      </div>
    </td>
  </tr>
</template>

<script lang="ts" setup>
import Api from "@/services/api/ApiServiceFabrick";
import SpotCreateModal from "@/components/SpotParts/SpotCreateModal.vue";
import {
  WORK_EVENT_TYPES,
} from "@/services/consts";
import {
  round,
  dateToDayAndTime,
} from "@/services/functions";
import {
  getAddress,
} from "@/services/Geocoder";
import WorkloadUpdateModal from "@/components/DailyReportUpdateModalParts/WorkloadUpdateModal";

import { ref, computed } from "vue";
import i18n from '@/lang/i18n';

const SPOT_READONLY_KEYS = ['management_office', 'operation_office', 'address', 'map'];

const props = defineProps({
  modelValue: {},
  index: { type: Number },
  operationOffice: {},
  updateAuthority: {},
  editable: {
    type: Boolean,
  },
  showFlash: {
    type: Function,
  },
  createSpotAuthority: {
    type: Boolean,
  },
  isWaitingForSave: {
    type: Boolean,
    default: false
  },
})

const emits = defineEmits([
  "update:modelValue",
  "setRefuelingAmount",
  "startEdit",
  "remove"
]);

const item = ref(props.modelValue);

const leavingTime = computed(() => {
  if (item.value.work == 'leaving_warehouse') {
    return ''
  }
  if (!item.value.start_time) {
    return ''
  }
  return dateToDayAndTime(new Date(item.value.start_time))
});
const workingTime = computed(() => {
  if (item.value.work == 'leaving_warehouse') {
    return ''
  }
  return item.value.working_time
})
const drivingTime = computed(() => {
  if (item.value.work == 'leaving_warehouse') {
    return ''
  }
  return item.value.driving_time
})
const returningTime = computed(() => {
  if (item.value.work == 'returning_warehouse') {
    return ''
  }
  if (!item.value.end_time) {
    return ''
  }
  return dateToDayAndTime(new Date(item.value.end_time))
})
const mileage = computed(() => {
  if (item.value.work == 'leaving_warehouse') {
    return ''
  }
  if (item.value.mileage == '-' || item.value.added_at) {
    return '-'
  }
  if (!item.value.mileage) {
    return '0.00'
  }
  return roundForView(item.value.mileage)
})
const isDisplayEditButton = computed(() => {
  return item.value.work != 'leaving_warehouse' &&
    props.editable &&
    props.updateAuthority
})

const showWorkloadButton = computed(() => {
  return item.value.work == 'loading' ||
    item.value.work == 'unloading'  ||
    item.value.work == 'refueling';
})
const isDisplayDeleteButton = computed(() => {
  return item.value.work != 'leaving_warehouse' && (
    item.value.adding ||
    item.value.allocated
  ) &&
  props.updateAuthority
})

const spotParams = computed(() => {
  return {
    operation_office_id: props.operationOffice.id,
    management_officeable_id: props.operationOffice.managementOfficeId,
    management_officeable_type: props.operationOffice.managementOfficeType,
    latitude: item.value.latitude,
    longitude: item.value.longitude,
    radius: null,
  }
})

const canCreateSpot = computed(() => {
  return props.createSpotAuthority &&
    !item.value.spot_id &&
    !(item.value.adding || item.value.work == 'stopping');
})

async function assignAddress(item) {
  if (item) {
    const result = await getAddress({
      lat: item.latitude,
      lng: item.longitude,
    });
    item.address = result;
  }
  return item;
}
function roundForView(num) {
  return round(num, 2).toFixed(2)
}

function setWorkId(workId) {
  item.value.work_id = workId;
  emits('update:modelValue', item.value);
}

function setRefuelingAmount() {
  emits("setRefuelingAmount")
}

function edit() {
  emits('startEdit');
}

function remove() {
  emits('remove');
}

function showFlashMessage(msg: string, flag: string, isAutoClose: boolean) {
  let className = 'info';
  let message = i18n.global.t('pages.dailyReports.messages.successRegistSpot');
  if (flag == 'error') {
    className = 'error';
    message = i18n.global.t('pages.dailyReports.errorMessage.registSpot');
  }

  props.showFlash(message, true, className, isAutoClose);
}

function callbackCreatedSpot(res) {
  Api().updateWorkSpot(item.value.delivery_plan_id, {
     work: item.value.work,
     date_time: item.value.start_time,
     spot_id: res.id,
  }).then(() => {
    item.value.spot_id = res.id;
  }).catch(() => {
    props.showFlash(i18n.global.t('pages.dailyReports.errorMessage.updateWorkSpot'), true, 'error', true);
  })
}
</script>

<style scoped>
.pin-drop{
  cursor:pointer
}
</style>
